/** Minimal shared UI kit (Tailwind). */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export function Card({ title, children, actions }: { title?: ReactNode; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
      {(title || actions) && (
        <div className="flex items-center justify-between mb-3">
          {title && <h2 className="text-lg font-semibold text-slate-800">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function Button({
  variant = 'primary',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' | 'ghost' }) {
  const styles: Record<string, string> = {
    primary: 'bg-brand text-white hover:bg-brand-dark',
    secondary: 'bg-slate-100 text-slate-800 hover:bg-slate-200',
    danger: 'bg-danger text-white hover:opacity-90',
    ghost: 'bg-transparent text-brand hover:bg-brand-light',
  };
  return (
    <button
      {...rest}
      className={`px-3 py-2 min-h-[40px] rounded-lg text-sm font-medium disabled:opacity-50 transition ${styles[variant]} ${rest.className ?? ''}`}
    >
      {children}
    </button>
  );
}

export function Badge({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'danger' | 'ok'; children: ReactNode }) {
  const tones: Record<string, string> = {
    info: 'bg-blue-100 text-blue-800',
    warn: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
    ok: 'bg-green-100 text-green-800',
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

export function severityTone(severity: string): 'info' | 'warn' | 'danger' | 'ok' {
  if (severity === 'critical' || severity === 'urgent') return 'danger';
  if (severity === 'warning') return 'warn';
  return 'info';
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block mb-3">
      <span className="block text-sm font-medium text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

export const inputClass =
  'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand';

export function Table({ headers, children }: { headers: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="text-left text-slate-500 border-b border-slate-200">
            {headers.map((h) => (
              <th key={h} className="py-2 pr-4 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Loading() {
  return <div className="text-slate-400 p-4">…</div>;
}

export function EmptyState({ message }: { message: string }) {
  return <div className="text-slate-400 text-sm p-4">{message}</div>;
}
